import React, { useState, useEffect } from "react";
import { getWatchlist, removeFromWatchlist, migrateLegacyWatchlist, exportWatchlistMd } from "../lib/watchlist";
import { ZoneChip, LageBadge, Button, Spinner } from "../components/ui";
import api, { formatApiError } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Bookmark, FileDown, Trash2, StickyNote, Crosshair } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

export default function Watchlist() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [noteError, setNoteError] = useState("");
  const [err, setErr] = useState(null);
  const nav = useNavigate();

  const refresh = async () => {
    const list = await getWatchlist();
    setItems(list);
  };

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      await migrateLegacyWatchlist();
      await refresh();
    } catch (e) {
      console.error("Watchlist load failed", e);
      setErr(t("common.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (e, id) => {
    e.stopPropagation();
    await removeFromWatchlist(id);
    await refresh();
  };

  const toggleNote = (e, id) => {
    e.stopPropagation();
    setNoteError("");
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    setDraft(items.find((i) => i.id === id)?.note || "");
  };

  const saveNote = async (id) => {
    setSaving(true);
    setNoteError("");
    try {
      await api.put(`/watchlist/${id}/note`, { note: draft });
      await refresh();
      setExpandedId(null);
    } catch (e) {
      setNoteError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10"><Spinner /></div>;
  if (err) return (
    <div className="p-10 text-center">
      <p className="text-sm text-signal-red font-mono">{err}</p>
      <button onClick={load} className="mt-3 text-xs font-mono uppercase tracking-widest2 text-signal-green hover:underline">
        {t("common.retry")}
      </button>
    </div>
  );

  const exportMd = () => {
    const md = exportWatchlistMd(items);
    const url = URL.createObjectURL(new Blob([md], { type: "text/markdown" }));
    const a = document.createElement("a");
    a.href = url; a.download = "immo-watch-watchlist.md"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="reveal">
      <div className="px-6 md:px-10 pt-8 pb-5 border-b border-border bg-grid flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline">{t("watchlist.eyebrow")}</div>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">{t("watchlist.title")}</h1>
          <p className="mt-2 text-xs text-text-muted">{t("watchlist.savedCount", { count: items.length, unit: items.length === 1 ? t("watchlist.savedOne") : t("watchlist.savedMany") })}</p>
        </div>
        {items.length > 0 && (
          <button
            onClick={exportMd}
            data-testid="watchlist-export-md"
            className="inline-flex items-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-widest2 border border-zinc-700 text-text-secondary hover:border-white hover:text-white"
          >
            <FileDown size={14} /> {t("watchlist.exportMd")}
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="p-16 text-center">
          <Bookmark size={36} className="mx-auto text-text-muted" />
          <div className="overline mt-4">{t("watchlist.noSavedTitle")}</div>
          <p className="mt-2 text-text-secondary text-sm max-w-md mx-auto">
            {t("watchlist.noSavedBody1")} <span className="text-white font-mono">{t("watchlist.saveToWatchlist")}</span> {t("watchlist.noSavedBody2")}
          </p>
        </div>
      ) : (
        <div className="border border-border">
          <div className="hidden md:grid md:grid-cols-12 text-[10px] font-mono uppercase tracking-widest2 text-text-muted border-b border-border bg-surface">
            <div className="col-span-1 p-3">{t("watchlist.colZone")}</div>
            <div className="col-span-3 p-3">{t("watchlist.colMarketAddress")}</div>
            <div className="col-span-1 p-3">{t("watchlist.colLage")}</div>
            <div className="col-span-1 p-3 text-right">{t("watchlist.colFaktor")}</div>
            <div className="col-span-2 p-3 text-right">{t("watchlist.colPrice")}</div>
            <div className="col-span-1 p-3 text-right">{t("watchlist.colCashflow")}</div>
            <div className="col-span-1 p-3">{t("watchlist.colSaved")}</div>
            <div className="col-span-2 p-3" />
          </div>
          {items.map((l) => (
            <div key={l.id} className="border-b border-border">
              <div
                onClick={() => nav(`/listings/${l.id}`)}
                onKeyDown={(e) => { if (e.key === "Enter") nav(`/listings/${l.id}`); }}
                role="button"
                tabIndex={0}
                className="block w-full text-left cursor-pointer hover:bg-surfaceHover transition-colors md:grid md:grid-cols-12 md:items-center"
              >
                {/* Mobile: stacked card */}
                <div className="p-3 space-y-1.5 md:hidden">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <ZoneChip zone={l.zone} />
                      <LageBadge grade={l.lage_grade} />
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => toggleNote(e, l.id)}
                        className={`p-1.5 transition-colors ${l.note ? "text-signal-green" : "text-text-muted hover:text-white"}`}
                        title={l.note ? t("watchlist.editNoteTitle") : t("watchlist.addNoteTitle")}
                      >
                        <StickyNote size={14} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); nav(`/bewerten?id=${l.id}`); }}
                        className="p-1.5 text-text-muted hover:text-signal-green transition-colors"
                        title={t("listingDetail.openBewerter")}
                      >
                        <Crosshair size={14} />
                      </button>
                      <button
                        onClick={(e) => remove(e, l.id)}
                        className="p-1.5 text-text-muted hover:text-signal-red transition-colors"
                        title={t("watchlist.removeTitle")}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-white">{l.market_name}</div>
                  <div className="text-[10px] text-text-muted truncate">{l.address}</div>
                  <div className="flex items-center justify-between text-[10px] font-mono text-text-muted">
                    <span>Faktor <span className="num text-white">{l.faktor != null ? l.faktor.toFixed(1) : "—"}</span></span>
                    <span className="num text-white">€{(l.price || 0).toLocaleString()}</span>
                    <span className={(l.monthly_cashflow ?? 0) >= 0 ? "text-signal-green" : "text-signal-red"}>
                      {l.monthly_cashflow != null ? `${l.monthly_cashflow >= 0 ? "+" : ""}€${l.monthly_cashflow}/mo` : "—"}
                    </span>
                  </div>
                </div>
                {/* Desktop: table row */}
                <div className="hidden md:contents">
                  <div className="col-span-1 p-3"><ZoneChip zone={l.zone} /></div>
                  <div className="col-span-3 p-3">
                    <div className="text-xs text-white">{l.market_name}</div>
                    <div className="text-[10px] text-text-muted truncate">{l.address}</div>
                  </div>
                  <div className="col-span-1 p-3"><LageBadge grade={l.lage_grade} /></div>
                  <div className="col-span-1 p-3 num text-right text-white">{l.faktor != null ? l.faktor.toFixed(1) : "—"}</div>
                  <div className="col-span-2 p-3 num text-right text-white">€{(l.price || 0).toLocaleString()}</div>
                  <div className={`col-span-1 p-3 num text-right ${(l.monthly_cashflow ?? 0) >= 0 ? "text-signal-green" : "text-signal-red"}`}>
                    {l.monthly_cashflow != null ? `${l.monthly_cashflow >= 0 ? "+" : ""}€${l.monthly_cashflow}/mo` : "—"}
                  </div>
                  <div className="col-span-1 p-3 num text-[10px] text-text-muted">{new Date(l.added_at).toLocaleDateString("en-GB")}</div>
                  <div className="col-span-2 p-3 flex justify-end gap-1">
                    <button
                      onClick={(e) => toggleNote(e, l.id)}
                      className={`p-1.5 transition-colors ${l.note ? "text-signal-green" : "text-text-muted hover:text-white"}`}
                      title={l.note ? t("watchlist.editNoteTitle") : t("watchlist.addNoteTitle")}
                    >
                      <StickyNote size={13} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); nav(`/bewerten?id=${l.id}`); }}
                      className="p-1.5 text-text-muted hover:text-signal-green transition-colors"
                      title={t("listingDetail.openBewerter")}
                      data-testid={`wl-bewerter-${l.id}`}
                    >
                      <Crosshair size={13} />
                    </button>
                    <button
                      onClick={(e) => remove(e, l.id)}
                      className="p-1.5 text-text-muted hover:text-signal-red transition-colors"
                      title={t("watchlist.removeTitle")}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>

              {expandedId === l.id && (
                <div className="px-4 pb-4 pt-1 bg-surface border-t border-border">
                  <div className="overline">{t("watchlist.noteForAi")}</div>
                  <p className="mt-1 text-[10px] text-text-muted">
                    {t("watchlist.noteHint")}
                  </p>
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={t("watchlist.notePlaceholder")}
                    rows={3}
                    maxLength={2000}
                    className="mt-2 w-full bg-bg border border-border px-3 py-2 text-sm font-mono text-white focus:border-white outline-none resize-none"
                  />
                  {noteError && <div className="mt-1 text-[10px] font-mono text-signal-red">{noteError}</div>}
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-[10px] font-mono text-text-muted">
                      {l.note_expires_at
                        ? t("watchlist.noteExpires", { date: new Date(l.note_expires_at).toLocaleDateString("en-GB") })
                        : t("watchlist.noteNoneYet")}
                    </span>
                    <Button onClick={() => saveNote(l.id)} variant="ghost" disabled={saving}>
                      {saving ? <Spinner size={12} /> : t("watchlist.saveNote")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
